import React from "react";
import type { ComponentProps } from "react";
import ReactMarkdown from "react-markdown";
import type { ExtraProps } from "react-markdown";
import remarkGfm from "remark-gfm";

import "./MarkdownContent.css";

export interface MarkdownContentProps {
  content: string;
  className?: string;
}

export const MarkdownContent: React.FC<MarkdownContentProps> = ({ content, className = "" }) => {
  return (
    <div className={`markdown-content ${className}`.trim()}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          pre: ({ node: _node, children, ...props }: ComponentProps<"pre"> & ExtraProps) => {
            const codeChild = React.Children.only(children);

            if (React.isValidElement<ComponentProps<"code">>(codeChild)) {
              const existingClassName = codeChild.props.className || "";
              return (
                <pre className="markdown-pre" {...props}>
                  {React.cloneElement(codeChild, {
                    className: `markdown-code-block ${existingClassName}`.trim(),
                  })}
                </pre>
              );
            }

            return (
              <pre className="markdown-pre" {...props}>
                {children}
              </pre>
            );
          },

          code: ({ node: _node, className, children, ...props }: ComponentProps<"code"> & ExtraProps) => (
            <code className={`markdown-inline-code ${className || ""}`.trim()} {...props}>
              {children}
            </code>
          ),

          table: ({ node: _node, children, ...props }: ComponentProps<"table"> & ExtraProps) => (
            <div className="markdown-table-wrapper">
              <table className="markdown-table" {...props}>
                {children}
              </table>
            </div>
          ),

          h1: ({ node: _node, children, ...props }: ComponentProps<"h1"> & ExtraProps) => (
            <h1 className="markdown-h1" {...props}>
              {children}
            </h1>
          ),
          h2: ({ node: _node, children, ...props }: ComponentProps<"h2"> & ExtraProps) => (
            <h2 className="markdown-h2" {...props}>
              {children}
            </h2>
          ),
          h3: ({ node: _node, children, ...props }: ComponentProps<"h3"> & ExtraProps) => (
            <h3 className="markdown-h3" {...props}>
              {children}
            </h3>
          ),
          h4: ({ node: _node, children, ...props }: ComponentProps<"h4"> & ExtraProps) => (
            <h4 className="markdown-h4" {...props}>
              {children}
            </h4>
          ),

          blockquote: ({ node: _node, children, ...props }: ComponentProps<"blockquote"> & ExtraProps) => (
            <blockquote className="markdown-blockquote" {...props}>
              {children}
            </blockquote>
          ),

          ul: ({ node: _node, children, ...props }: ComponentProps<"ul"> & ExtraProps) => (
            <ul className="markdown-ul" {...props}>
              {children}
            </ul>
          ),
          ol: ({ node: _node, children, ...props }: ComponentProps<"ol"> & ExtraProps) => (
            <ol className="markdown-ol" {...props}>
              {children}
            </ol>
          ),

          a: ({ node: _node, children, href, ...props }: ComponentProps<"a"> & ExtraProps) => (
            <a
              className="markdown-link"
              href={href}
              target={href?.startsWith("http") ? "_blank" : undefined}
              rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
              {...props}
            >
              {children}
            </a>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
